import { FC, useCallback, useState } from 'react';
import { isNil } from 'ramda';

import useErrorHandledMutation, {
  throwOnlyUnhandledErrors,
} from 'common/hooks/useErrorHandledMutation';
import { isValidEmail } from 'components/LegacyInput/utils';
import useSendCompanyInvitation from 'features/Onboarding/common/useSendCompanyInvitation';

import {
  beneficiariesDocsStatusQuery,
  createPhysicalPersonLegalBeneficiaryMutation,
} from '../graphql';
import {
  CreatePhysicalPersonLegalBeneficiaryData,
  CreatePhysicalPersonLegalBeneficiaryInput,
  NewBeneficiaryCardProps,
} from '../types';
import Component from './Component';

const NewBeneficiaryCard: FC<NewBeneficiaryCardProps> = ({
  companyProfileId,
  removeCard,
}) => {
  const [values, setValues] = useState({
    firstName: '',
    gender: null,
    inviteeEmail: '',
    isDirector: null,
    isMajorShareholder: null,
    lastName: '',
  });

  const [loading, setLoading] = useState(false);

  const handleChange = <T extends string | boolean>(name: string, value: T) => {
    setValues({
      ...values,
      [name]: value,
    });
  };

  const { sendCompanyInvitation } = useSendCompanyInvitation({
    awaitRefetchQueries: true,
    refetchQueries: () => [
      {
        query: beneficiariesDocsStatusQuery,
        variables: {
          companyProfileId,
        },
      },
    ],
  });

  const [mutator] = useErrorHandledMutation<
    CreatePhysicalPersonLegalBeneficiaryData,
    CreatePhysicalPersonLegalBeneficiaryInput
  >(createPhysicalPersonLegalBeneficiaryMutation);

  const hasSetEligibility =
    !isNil(values.isDirector) && !isNil(values.isMajorShareholder);
  const isValidBeneficiary =
    hasSetEligibility &&
    Boolean(values.isDirector || values.isMajorShareholder);
  const isFormValid =
    isValidBeneficiary &&
    values.gender !== null &&
    values.firstName !== '' &&
    values.lastName !== '' &&
    isValidEmail(values.inviteeEmail);

  const createLegalBeneficiary = useCallback(async () => {
    if (loading) {
      return;
    }
    // gender being required, we should never end up here
    if (!isValidBeneficiary) {
      throw new Error(
        'every field should be set and beneficiary should be director or major shareholder',
      );
    }
    if (companyProfileId === undefined) {
      throw new Error('companyProfileId should be defined');
    }

    setLoading(true);
    const result = await mutator({
      variables: {
        input: {
          companyProfileId,
          ...values,
          gender: values.gender || '',
          inviteeEmail: values.inviteeEmail || undefined,
        },
      },
    }).catch(throwOnlyUnhandledErrors);

    if (result && result.data) {
      const {
        data: {
          createPhysicalPersonLegalBeneficiary: {
            invitation: { companyInvitationId },
          },
        },
      } = result;
      await sendCompanyInvitation({
        companyInvitationId,
        companyProfileId,
      });
    }
    removeCard();
    setLoading(false);
  }, [
    companyProfileId,
    isValidBeneficiary,
    loading,
    mutator,
    removeCard,
    sendCompanyInvitation,
    values,
  ]);

  return (
    <Component
      hasSetEligibility={hasSetEligibility}
      isFormValid={isFormValid}
      isValidBeneficiary={isValidBeneficiary}
      loading={loading}
      onChange={handleChange}
      onRemove={removeCard}
      onSubmit={createLegalBeneficiary}
      values={values}
    />
  );
};

export default NewBeneficiaryCard;
